import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Chip, Alert, LinearProgress,
  IconButton, Table, TableHead, TableRow, TableCell, TableBody, Tooltip,
  TablePagination, Stack, useMediaQuery, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  PlayArrow as ResumeIcon,
  ArrowBack as BackIcon,
  CheckCircle as DoneIcon,
  ErrorOutlined as FailIcon,
  Sync as RunningIcon,
  HighlightOff as SkippedIcon,
  HourglassBottom as CancellingIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonSearch as DiscoveryIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import {
  getContactDiscoveryRun, listContactDiscoveryItems, cancelContactDiscoveryRun,
  resumeContactDiscoveryRun, updateContactDiscoveryRun, deleteContactDiscoveryRun,
} from '../services/api';
import { useDialog } from '../components/DialogProvider';

const STATUS_CHIP_COLOR = {
  Pending: 'default',
  Running: 'info',
  Cancelling: 'warning',
  Completed: 'success',
  Failed: 'error',
  Cancelled: 'warning',
};

const ITEM_STATUS_COLOR = {
  Pending: 'default',
  Processing: 'info',
  Succeeded: 'success',
  Failed: 'error',
  Skipped: 'warning',
};

function fmtSecs(s) {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ${s % 60}s`;
}

function fmtDuration(start, end, accumulatedSeconds) {
  const acc = accumulatedSeconds || 0;
  if (end) {
    if (acc > 0) return fmtSecs(acc);
    if (start) {
      const s = Math.max(0, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000));
      return fmtSecs(s);
    }
    return '—';
  }
  if (!start) return acc > 0 ? fmtSecs(acc) : '—';
  const live = Math.max(0, Math.round((Date.now() - new Date(start).getTime()) / 1000));
  return fmtSecs(acc + live);
}

const isTerminal = (s) => s === 'Completed' || s === 'Failed' || s === 'Cancelled';

const STATUS_FILTERS = ['All', 'Pending', 'Processing', 'Succeeded', 'Failed', 'Skipped'];

export default function ContactDiscoveryJobDetailPage() {
  const { id } = useParams();
  const jobId = parseInt(id, 10);
  const navigate = useNavigate();
  const dialog = useDialog();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [job, setJob] = useState(null);
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ intervalSeconds: '', winnersPerLead: '' });
  const pollRef = useRef(null);

  const loadJob = useCallback(async () => {
    try {
      const { data } = await getContactDiscoveryRun(jobId);
      setJob(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load job');
    }
  }, [jobId]);

  const loadItems = useCallback(async () => {
    try {
      const params = {
        skip: page * rowsPerPage,
        take: rowsPerPage,
      };
      if (statusFilter && statusFilter !== 'All') params.status = statusFilter;
      const { data } = await listContactDiscoveryItems(jobId, params);
      setItems(data || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load items');
    }
  }, [jobId, page, rowsPerPage, statusFilter]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadJob(), loadItems()]).finally(() => setLoading(false));
  }, [loadJob, loadItems]);

  useEffect(() => {
    if (!job || isTerminal(job.status)) return;
    const tick = async () => {
      await loadJob();
      await loadItems();
    };
    pollRef.current = setInterval(tick, 2000);
    return () => clearInterval(pollRef.current);
  }, [job, loadJob, loadItems]);

  const handleCancel = async (force = false) => {
    const ok = await dialog.confirm({
      title: force ? 'Force-cancel job' : 'Cancel job',
      message: force
        ? 'Force-cancel marks the job Cancelled immediately and skips any in-flight lead. Use only if the job appears stuck.'
        : 'Stop after the current lead. Pending leads will be marked Skipped.',
      confirmText: force ? 'Force cancel' : 'Cancel job',
      danger: force,
    });
    if (!ok) return;
    try {
      await cancelContactDiscoveryRun(jobId, force);
      await loadJob();
      await loadItems();
    } catch (e) {
      setError(e?.response?.data?.message || 'Cancel failed');
    }
  };

  const handleResume = async () => {
    try {
      await resumeContactDiscoveryRun(jobId);
      await loadJob();
      await loadItems();
    } catch (e) {
      setError(e?.response?.data?.message || 'Resume failed');
    }
  };

  const handleDelete = async () => {
    const ok = await dialog.confirm({
      title: 'Delete discovery job',
      message: `Delete job #${jobId}? Job + per-lead rows will be removed. Leads + their contacts are not affected.`,
      confirmText: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteContactDiscoveryRun(jobId);
      navigate('/leads/contact-discovery');
    } catch (e) {
      setError(e?.response?.data?.message || 'Delete failed');
    }
  };

  const openEdit = () => {
    setEditForm({
      intervalSeconds: String(job?.intervalSeconds ?? ''),
      winnersPerLead: String(job?.winnersPerLead ?? ''),
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const body = {};
      if (editForm.intervalSeconds !== '') body.intervalSeconds = parseInt(editForm.intervalSeconds, 10);
      if (editForm.winnersPerLead !== '') body.winnersPerLead = parseInt(editForm.winnersPerLead, 10);
      await updateContactDiscoveryRun(jobId, body);
      setEditOpen(false);
      await loadJob();
    } catch (e) {
      setError(e?.response?.data?.message || 'Update failed');
    }
  };

  if (loading && !job) {
    return <Box sx={{ p: 3 }}><Typography>Loading…</Typography></Box>;
  }
  if (!job) {
    return <Box sx={{ p: 3 }}><Alert severity="error">Job not found.</Alert></Box>;
  }

  const progress = job.progressPercent ?? (job.totalLeads > 0 ? (100 * job.processedLeads / job.totalLeads) : 0);
  const canCancel = !isTerminal(job.status);
  const canResume = job.status === 'Cancelled' || job.status === 'Failed';
  const canEdit = !isTerminal(job.status) && job.status !== 'Cancelling';
  const canDelete = isTerminal(job.status);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={() => navigate('/leads/contact-discovery')}>
          <BackIcon />
        </IconButton>
        <DiscoveryIcon color="primary" />
        <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.25rem' }}>
          Contact Discovery #{job.id}
        </Typography>
        <Chip size="small" label={job.status} color={STATUS_CHIP_COLOR[job.status] || 'default'} sx={{ fontWeight: 600 }} />
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={loadJob} title="Refresh"><RefreshIcon fontSize="small" /></IconButton>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
          {canCancel && (
            <Button size="small" variant="outlined" color="warning"
              startIcon={job.status === 'Cancelling' ? <CancellingIcon /> : <CancelIcon />}
              onClick={() => handleCancel(false)}
              disabled={job.status === 'Cancelling'}
              sx={{ textTransform: 'none', borderRadius: 1.5 }}>
              {job.status === 'Cancelling' ? 'Cancelling…' : 'Cancel'}
            </Button>
          )}
          {canCancel && (
            <Button size="small" variant="text" color="error"
              onClick={() => handleCancel(true)}
              sx={{ textTransform: 'none' }}>
              Force cancel
            </Button>
          )}
          {canResume && (
            <Button size="small" variant="contained" startIcon={<ResumeIcon />}
              onClick={handleResume}
              sx={{ textTransform: 'none', borderRadius: 1.5 }}>
              Resume
            </Button>
          )}
          {canEdit && (
            <Button size="small" variant="outlined" startIcon={<EditIcon />}
              onClick={openEdit}
              sx={{ textTransform: 'none', borderRadius: 1.5 }}>
              Edit
            </Button>
          )}
          {canDelete && (
            <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
              onClick={handleDelete}
              sx={{ textTransform: 'none', borderRadius: 1.5 }}>
              Delete
            </Button>
          )}
        </Stack>

        <LinearProgress
          variant="determinate"
          value={Math.min(100, progress)}
          sx={{ height: 10, borderRadius: 1, mb: 1.5 }}
        />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 3 }, mb: 1 }}>
          <Stat label="Engines" value={(job.providerNames || []).join('+') || 'default'} mono />
          <Stat label="Winners/Lead" value={job.winnersPerLead} />
          <Stat label="Force re-discover" value={job.forceRediscover ? 'Yes' : 'No'} />
          <Stat label="Interval" value={`${job.intervalSeconds}s`} />
          <Stat label="Total" value={job.totalLeads} />
          <Stat label="Processed" value={`${job.processedLeads} / ${job.totalLeads}`} />
          <Stat label="Succeeded" value={job.succeededLeads} color="#16a34a" />
          <Stat label="Failed" value={job.failedLeads} color={job.failedLeads > 0 ? '#dc2626' : '#94a3b8'} />
          <Stat label="Skipped" value={job.skippedLeads} color={job.skippedLeads > 0 ? '#f59e0b' : '#94a3b8'} />
          <Stat label="Contacts created" value={job.contactsCreatedTotal} color="#0284c7" bold />
          <Stat label="Started" value={job.startedAt ? new Date(job.startedAt).toLocaleString() : '—'} />
          <Stat label="Duration" value={fmtDuration(job.startedAt, job.finishedAt, job.accumulatedSeconds)} />
        </Box>

        {job.errorMessage && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {job.errorMessage}
          </Alert>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ color: '#475569', flex: 1 }}>Per-lead results</Typography>
          <ToggleButtonGroup
            size="small"
            value={statusFilter}
            exclusive
            onChange={(_, v) => v && (setStatusFilter(v), setPage(0))}
          >
            {STATUS_FILTERS.map((s) => (
              <ToggleButton key={s} value={s} sx={{ textTransform: 'none', px: 1.5 }}>
                {s}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lead</TableCell>
                <TableCell>Status</TableCell>
                {!isMobile && <TableCell>Website</TableCell>}
                <TableCell align="right">Found</TableCell>
                <TableCell align="right">Winners</TableCell>
                {!isMobile && <TableCell>Engines</TableCell>}
                {!isMobile && <TableCell>Started</TableCell>}
                {!isMobile && <TableCell>Finished</TableCell>}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isMobile ? 5 : 9} sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                    No items match the current filter.
                  </TableCell>
                </TableRow>
              )}
              {items.map((it) => (
                <TableRow key={it.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      <RouterLink to={`/leads/${it.leadId}`} style={{ textDecoration: 'none', color: '#6366f1' }}>
                        #{it.leadId} {it.companyName}
                      </RouterLink>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={it.status} color={ITEM_STATUS_COLOR[it.status] || 'default'} />
                  </TableCell>
                  {!isMobile && (
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {it.website || <em style={{ color: '#94a3b8' }}>—</em>}
                    </TableCell>
                  )}
                  <TableCell align="right">{it.contactsFound ?? 0}</TableCell>
                  <TableCell align="right">
                    <Box component="span" sx={{ color: '#0284c7', fontWeight: 700 }}>{it.winnersAssigned ?? 0}</Box>
                  </TableCell>
                  {!isMobile && (
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {it.providersUsed || '—'}
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell sx={{ fontSize: 12 }}>
                      {it.startedAt ? new Date(it.startedAt).toLocaleTimeString() : '—'}
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell sx={{ fontSize: 12 }}>
                      {it.finishedAt ? new Date(it.finishedAt).toLocaleTimeString() : '—'}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <Tooltip title="Open lead">
                      <IconButton size="small" component={RouterLink} to={`/leads/${it.leadId}`}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <TablePagination
          component="div"
          count={-1}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelDisplayedRows={({ from, to }) => `${from}–${to}`}
        />
      </Paper>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit job</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              type="number"
              label="Interval (sec)"
              inputProps={{ min: 0, max: 3600 }}
              value={editForm.intervalSeconds}
              onChange={(e) => setEditForm((f) => ({ ...f, intervalSeconds: e.target.value }))}
            />
            <TextField
              size="small"
              type="number"
              label="Winners per lead"
              inputProps={{ min: 1, max: 10 }}
              value={editForm.winnersPerLead}
              onChange={(e) => setEditForm((f) => ({ ...f, winnersPerLead: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handleSaveEdit}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Stat({ label, value, color, mono, bold }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>{label}</Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: bold ? 700 : 600,
          color: color || '#0f172a',
          fontFamily: mono ? 'monospace' : 'inherit',
        }}
      >
        {value ?? '—'}
      </Typography>
    </Box>
  );
}
